import React, { act, useEffect, useState } from "react";
import axios from "axios";
import { Badge, Flex, Button, Box, Text, UnstyledButton } from "@mantine/core";
import toast from "react-hot-toast";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import DealerVerification from "./DealerVerification";
import UnverifiedItem from "./UnverifiedItem";
import OdometerValueMismatch from "./OdometerValueMismatch";
import DealerWarnings from "./DealerWarnings";
import DealerMode from "./DealerMode";
import OdometerMismatchModal from "./OdometerMismatchModal";
import OdometerForm from "./OdometerForm";

export default function UnverifiedItems({
  items,
  additionalReqData,
  fetchData,
  verificationCode,
  administrationData = null,
  codeVerificationData,
  children,
  isVehicleDataEffort = false,
}) {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState(null);
  const [dealerCodeData, setDealerCodeData] = useState(null);
  const [value, setValue] = useState("");
  const [dealerSubmitting, setDealerSubmitting] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [odometerMismatchModalOpen, setOdometerMismatchModalOpen] =
    useState(false);

  const locationNeeded =
    administrationData &&
    (administrationData.admin_id || administrationData.organization_id);

  useEffect(() => {
    setDealerCodeData(null);
    setValue("");
    setDealerSubmitting(false);

    if (verificationCode) {
      setDealerCodeData(verificationCode);
    }
  }, [activeItem]);

  function resolveOdometerMismatch(val) {
    setValue(val);
    setOdometerMismatchModalOpen(false);
    onDealerSubmit(val);
  }

  function onDealerSubmit(odometerValueOverride = null) {
    const odometerValue =
      odometerValueOverride !== null ? odometerValueOverride : value;

    if (
      activeItem.odometer_value &&
      activeItem.odometer_value !== parseInt(odometerValue) &&
      !odometerMismatchModalOpen
    ) {
      setOdometerMismatchModalOpen(true);
      return;
    }
    setDealerSubmitting(true);

    const req = administrationData
      ? {
          ...administrationData,
          campaign_effort_id: activeItem.campaign_effort_id,
          odometer_value: odometerValue,
          ride_challenge_data_point_id: activeItem.id,
          user_vehicle_id: activeItem.user_vehicle_id,
        }
      : {
          ...additionalReqData,
          verifier_remote_code_id: dealerCodeData.id,
          odometer_value: odometerValue,
          ride_challenge_data_point_id: activeItem.id,
          user_vehicle_id: activeItem.user_vehicle_id,
        };

    if (!activeItem.user_vehicle_id) {
      req.vin = activeItem.vin;
      req.checksum_results = activeItem.checksum_results;
      req.nhtsa_results = activeItem.nhtsa_results;
      req.year = activeItem.year;
      req.make = activeItem.make;
      req.model = activeItem.model;
      req.series = activeItem.series;
    } else {
      if (activeItem?.user_vehicle?.odometer_rolled) {
        req.odometer_value = parseInt(odometerValue) + 1000000;
      }
    }

    if (selectedLocationId) {
      req.location_id = selectedLocationId;
    }

    axios
      .post(`/ride-challenge/verify-reading/`, req)
      .then(() => {
        toast.success(t("common:verified") + "!");
        setDealerSubmitting(false);
        // fetchUser();
        setActiveItem(null);
        setSelectedLocationId("");
        fetchData();
      })
      .then(() => fetchData())
      .catch((err) => {
        toast.error(err);
        setDealerSubmitting(false);
      });
  }

  function onDeleteClick(itemId) {
    axios
      .post(`/ride-challenge/inactivate-data-point/`, {
        ride_challenge_data_point_id: itemId,
      })
      .then(() => {
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  if (items.length === 0) {
    return (
      <Text c="white" align="center">
        {t("microsite:unverified_items_empty_text")}
      </Text>
    );
  }

  if (activeItem) {
    const odometerRolled = activeItem?.user_vehicle?.odometer_rolled || false;
    const odometerValueNormalized = odometerRolled
      ? activeItem.odometer_value >= 1000000
        ? activeItem.odometer_value - 1000000
        : activeItem.odometer_value
      : activeItem.odometer_value;

    return (
      <div>
        {/* <Box>
          <Text c="white" fw={600} size="xl" align="center" mb="xl">
            {activeItem.vin}
          </Text>
        </Box> */}
        {dealerCodeData ? (
          <>
            {activeItem.user_vehicle_id ? (
              <DealerMode border={false} dealerCodeData={dealerCodeData}>
                <div>
                  <DealerWarnings
                    reqData={{
                      user_id: activeItem.user_id,
                      campaign_effort_id: activeItem.campaign_effort_id,
                    }}
                  />
                  <Flex justify="center" mt="lg">
                    <OdometerForm value={value} onChange={(e) => setValue(e)} />
                  </Flex>
                  {activeItem.odometer_value && (
                    <OdometerValueMismatch
                      // originalValue={activeItem.odometer_value}
                      originalValue={odometerValueNormalized}
                      newValue={`${value}`}
                    />
                  )}
                  <Flex justify="center" mt="lg">
                    <Button
                      onClick={() => onDealerSubmit()}
                      disabled={!value}
                      loading={dealerSubmitting}
                    >
                      {t("mixer:vehicle:submit_reading")}
                    </Button>
                  </Flex>
                  {odometerMismatchModalOpen && (
                    <OdometerMismatchModal
                      userValue={activeItem.odometer_value}
                      dealerValue={value}
                      onClose={() => setOdometerMismatchModalOpen(false)}
                      onSubmit={(val) => resolveOdometerMismatch(val)}
                    />
                  )}
                </div>
              </DealerMode>
            ) : (
              <>
                <UnverifiedItem
                  info={activeItem}
                  dealerCodeData={dealerCodeData}
                  additionalReqData={{
                    ...additionalReqData,
                    verifier_remote_code_id: dealerCodeData.id,
                  }}
                  fetchUser={() => {
                    setActiveItem(null);
                    fetchData();
                  }}
                  isVehicleDataEffort={isVehicleDataEffort}
                />
              </>
            )}
          </>
        ) : (
          <>
            <DealerVerification
              codeVerificationData={codeVerificationData}
              setDealerCodeData={(e) => setDealerCodeData(e)}
            />
          </>
        )}
        <Flex justify="center" mt="xl" gap="xs">
          <Button
            c="dark"
            color="gray.1"
            size="xs"
            radius="lg"
            onClick={() => setActiveItem(null)}
          >
            {t("microsite:back_to_unverified_items_button")}
          </Button>
          {children && <>{children}</>}
        </Flex>
      </div>
    );
  }

  return (
    <div>
      {items.map((item, i) => {
        const source = item.user_vehicle || item;
        const odometerValue = source.odometer_rolled
          ? item.odometer_value > 1000000
            ? item.odometer_value - 1000000
            : item.odometer_value
          : item.odometer_value;

        return (
          <div key={item.id}>
            <Box
              mb="lg"
              style={{
                // backgroundColor: "var(--mantine-color-dark-6)",
                padding: "5px 10px 15px 10px",
                borderRadius: "4px",
                // border: "1px solid var(--mantine-color-dark-5)",
                background: "var(--mantine-color-dark-9)",
                border: "1px solid var(--mantine-color-dark-8)",
              }}
            >
              <Badge radius="sm" mb="xs" size="xs" variant="light">
                {item.vin
                  ? t("microsite:unverified_item_vin_verification")
                  : t("microsite:unverified_item_reading_verification")}
              </Badge>
              <Text c="white" fw={600}>
                {source.year} {source.make} {source.model}{" "}
                {source.series && `${source.series}`}
              </Text>
              <Text c="dimmed">
                {odometerValue} {item.use_kilometers ? "km" : "mi"}
              </Text>
              {source.vin && (
                <Text c="dimmed">
                  <span style={{ textTransform: "uppercase" }}>
                    {source.vin}
                  </span>
                </Text>
              )}
              {/* {locationNeeded && (
                <Box>
                  <StyledForm>
                    <LocationSelect
                      label="Location to verify at"
                      reqData={{
                        campaign_effort_id: item.campaign_effort_id,
                      }}
                      value={selectedLocationId}
                      onChange={(e) => setSelectedLocationId(e)}
                    />
                  </StyledForm>
                </Box>
              )} */}
              {!administrationData && (
                <Flex gap="xs" align="center" mt="sm">
                  <Button
                    size="xs"
                    onClick={() => setActiveItem(item)}
                    radius="lg"
                    disabled={
                      locationNeeded && !selectedLocationId ? true : false
                    }
                  >
                    {t("common:verify")}
                  </Button>
                  <DeleteButton onConfirmClick={() => onDeleteClick(item.id)} />
                </Flex>
              )}
            </Box>
          </div>
        );
      })}
      {children && <Box mt="xl">{children}</Box>}
    </div>
  );
}

const DeleteButton = ({ onConfirmClick }) => {
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);

  if (isConfirming) {
    return (
      <Flex justify="center" gap="xs">
        <Button
          radius="lg"
          variant="outline"
          size="xs"
          color="gray"
          onClick={() => setIsConfirming(false)}
        >
          {t("common:cancel")}
        </Button>
        <Button color="red" radius="lg" size="xs" onClick={onConfirmClick}>
          {t("common:confirm")}
        </Button>
      </Flex>
    );
  }

  return (
    <Button
      size="xs"
      onClick={() => setIsConfirming(true)}
      color="gray"
      radius="lg"
      variant="outline"
    >
      {t("common:delete")}
    </Button>
  );
};
